/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { useEffect, useState } from "react";

import { useRouter } from "next/router";

import VHTApp from "src/app/VHTApp";
import { Auth } from "src/classes/user/auth/Auth";
import { LoadingType, LoadingWrapper } from "src/components/Layout/LoadingWrapper";
import { AuthLevel } from "src/lib/constants";
import { LoadingStatus } from "src/lib/types";
type Props = {
  /** components to be rendered if RouteProtector determines they can be */
  children: JSX.Element;
};

export default function RouteProtector({ children }: Props) {
  const router = useRouter();
  const auth = Auth.instance;
  const authLevel = auth.observe((state) => state.authLevel);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.Idle);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    setLoadingStatus(LoadingStatus.Loading);
    auth
      .authenticate()
      .then(() => setLoadingStatus(LoadingStatus.Succeeded))
      .catch((err) => {
        setLoadingStatus(LoadingStatus.Failed);
        setErrorMessage(err.message || "Error while authenticating");
      });
  }, [authLevel]);

  return (
    <>
      {isMounted && (
        <LoadingWrapper status={loadingStatus} loadingType={LoadingType.Fullscreen} message={errorMessage}>
          {(() => {
            switch (authLevel) {
              case AuthLevel.Authorized:
                if (auth.isAuthorizedForRoute(router.route)) {
                  return <VHTApp>{children}</VHTApp>;
                }
                router.replace(auth.defaultRoute);
                return;
              case AuthLevel.Guest:
                if (auth.isAuthorizedForRoute(router.route)) {
                  return <>{children}</>;
                }
                router.replace(auth.defaultRoute);
                return;
              case AuthLevel.Onboarding:
                if (auth.isAuthorizedForRoute(router.route)) {
                  return <>{children}</>;
                } else {
                  router.replace(auth.defaultRoute);
                  return;
                }
            }
          })()}
        </LoadingWrapper>
      )}
    </>
  );
}
